import { apiSlice } from "../../../features/api/apiSlice";
export type CategoryLite = { id: number; name: string; is_active?: boolean };

export type AdminApplication = {
  id: string;
  surname?: string;
  other_names?: string;
  title?: string;
  status?: string;
  completion_step?: number;
  created_at?: string;

  application_category_id?: number;

  user?: {
    id?: string | number;
    name?: string;
    email?: string;
  };

  category?: {
    id: number;
    name: string;
  };
};

/**
 * =========
 * NESTED TYPES (based on your API response)
 * =========
 */
export type RefereeRequest = {
  id: string;
  probationer_application_id: string;
  member_id: string;
  relationship: string | null;

  status: "pending" | "accepted" | "rejected";
  remarks?: string | null;
  decided_at?: string | null;

  application?: any; // include nested applicant summary
  created_at?: string;
  updated_at?: string;
};

export type ProbationerQualification = {
  id: string;
  probationer_application_id: string;
  institution: string;
  qualification: string;
  cert_file: string | null;
  year: string;
  created_at: string;
  updated_at: string;
};

export type ProbationerSeminar = {
  id: string;
  probationer_application_id: string;
  title: string;
  date: string; // "YYYY-MM-DD"
  location: string | null;
  created_at: string;
  updated_at: string;
};

export type ProbationerProfessionalMembership = {
  id: string;
  probationer_application_id: string;
  body_name: string;
  stage_passed: string | null;
  certificate_path: string | null;
  created_at: string;
  updated_at: string;
};

export type ProbationerWorkExperience = {
  id: string;
  probationer_application_id: string;
  organization: string;
  position: string;
  start_date: string; // "YYYY-MM-DD"
  end_date: string | null;
  responsibilities: string | null;
  created_at: string;
  updated_at: string;
};

export type ProbationerReferee = {
  id?: string;
  probationer_application_id?: string;
  member_id: string;
  relationship: string | null;
  created_at?: string;
  updated_at?: string;
};

export type ProbationerReview = {
  id: string;
  probationer_application_id: string;
  // You didn't show fields, so keep flexible
  comment?: string | null;
  remarks?: string | null;
  status?: string | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
};

/**
 * =========
 * ROOT APPLICATION TYPE (for list + details)
 * =========
 */
export type ProbationerApplication = {
  id: string;
  user_id: number; // your response shows numeric: 3
  logbook_diet_id: string;

  surname: string;
  other_names: string;
  title: string;

  postal_address: string | null;
  residential_address: string;

  email: string;
  phone: string;

  date_of_birth: string; // "YYYY-MM-DD"
  nationality: string;

  membership_grade: string | null;
  membership_no: string | null;

  passport_photo: string | null; // may be null
  pro_pic?: string | null; // in step1 you store pro_pic
  signature?: string | null;

  completion_step: number;

  approved_at: string | null;
  rejected_at: string | null;
  rejected_reason: string | null;

  // The review ladder — see modules/application/utils/applicationStatus.ts.
  // `awaiting_endorsements` and `submitted` are older names for the
  // `acknowledged` and `under_review` rungs; still returned by old rows.
  status:
    | "pending"
    | "acknowledged"
    | "awaiting_endorsements"
    | "under_review"
    | "with_committee"
    | "submitted"
    | "approved"
    | "rejected"
    | "returned";

  /** Attached by the API to the single-application read, not a column. */
  fee_settled?: boolean;
  endorsements_outstanding?: string[];

  created_at: string;
  updated_at: string;

  qualifications: ProbationerQualification[];
  referees: ProbationerReferee[];
  seminars: ProbationerSeminar[];
  reviews: ProbationerReview[];

  // backend sometimes returns snake_case
  professional_memberships?: ProbationerProfessionalMembership[];
  work_experiences?: ProbationerWorkExperience[];

  // backend might also return camelCase depending on serializer
  professionalMemberships?: ProbationerProfessionalMembership[];
  workExperiences?: ProbationerWorkExperience[];
};

export type ApiMessage<T = any> = {
  message: string;
  data?: T;
  completion_step?: number;
};

/**
 * =========
 * INPUT TYPES (for step submissions)
 * =========
 */

export type QualificationItem = {
  institution: string;
  qualification: string;
  year: string;
};

export type OLevelResult = {
  exam_type: "waec" | "neco";
  exam_year: string;
  exam_number?: string | null;
  subjects: string[];
};

export type MembershipItem = {
  body_name: string;
  stage_passed?: string;
  certificate_path?: string;
};

export type ExperienceItem = {
  organization: string;
  position: string;
  start_date: string;
  end_date?: string;
  responsibilities?: string;
};

export type SeminarItem = {
  title: string;
  date: string;
  location?: string;
};

export type RefereeItem = {
  member_id: string;
  relationship?: string;
};

export type MemberLite = {
  id: string;
  surname?: string;
  other_names?: string;
  email?: string;
  phone?: string;
};

// response wrapper from your Laravel controller
type MemberSearchResponse = {
  status: "success";
  query_provided: string;
  count: number;
  data: MemberLite[];
};

/**
 * =========
 * API
 * =========
 */
export const probationerApplicationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAdminApplicationsByCategory: builder.query<
      AdminApplication[],
      { categoryId: number }
    >({
      query: ({ categoryId }) => ({
        url: `/probationer/applications/category?category_id=${categoryId}`,
        method: "GET",
      }),
      transformResponse: (res: any) => (Array.isArray(res) ? res : res?.data ?? []),
    }),
    // Total applications per category for a diet, plus the diet meta.
    getDietCategoryCounts: builder.query<
      { total: number; counts: Record<string, number>; diet: any | null },
      { dietId: string }
    >({
      query: ({ dietId }) => ({
        url: `/probationer/diets/${dietId}/category-counts`,
        method: "GET",
      }),
      transformResponse: (res: any) => ({
        total: Number(res?.total ?? 0),
        counts: (res?.data ?? res?.counts ?? {}) as Record<string, number>,
        diet: res?.diet ?? null,
      }),
    }),
    getActiveUserCategories: builder.query<CategoryLite[], void>({
      query: () => ({
        url: `/user-categories/active`, // ✅ adjust to your real endpoint
        method: "GET",
      }),
      transformResponse: (res: any): CategoryLite[] => {
        // supports: CategoryLite[] OR { data: CategoryLite[] }
        if (Array.isArray(res)) return res as CategoryLite[];
        if (Array.isArray(res?.data)) return res.data as CategoryLite[];
        return [];
      },
      providesTags: ["UserCategories"],
    }),

    searchMembersByQ: builder.query<MemberSearchResponse, string>({
      query: (q) => ({
        url: `/members/search/${encodeURIComponent(q)}`,
        method: "GET",
      }),
    }),

    // Referee/mentor picker: only financially up-to-date members are eligible
    // to be used as a reference. Same response shape as searchMembersByQ.
    searchUpToDateMembersByQ: builder.query<MemberSearchResponse, string>({
      query: (q) => ({
        url: `/members/search-up-to-date/${encodeURIComponent(q)}`,
        method: "GET",
      }),
    }),
    /**
     * LIST (also serves as "details data" since it includes nested relations)
     */
    getMyProbationerApplications: builder.query<ProbationerApplication[], void>(
      {
        query: () => ({
          url: "/probationer",
          method: "GET",
        }),

        // ✅ normalize response into an array
        transformResponse: (response: any) => {
          // case A: backend returns array directly
          if (Array.isArray(response)) return response;

          // case B: backend returns { data: [...] }
          if (Array.isArray(response?.data)) return response.data;

          // case C: backend returns { data: { data: [...] } } (pagination)
          if (Array.isArray(response?.data?.data)) return response.data.data;

          // case D: backend returns { applications: [...] }
          if (Array.isArray(response?.applications))
            return response.applications;

          // fallback: return empty list to avoid crash
          return response;
        },

        providesTags: ["ProbationerApplications" as any],
      }
    ),

    /**
     * STEP 1
     */
    createProbationerApplicationStep1: builder.mutation<
      ApiMessage<ProbationerApplication>,
      FormData
    >({
      query: (body) => ({
        url: "/probationer/step1",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ProbationerApplications" as any],
    }),

    /**
     * STEP 2
     */
    saveProbationerStep2Qualifications: builder.mutation<
      ApiMessage<ProbationerQualification[]>,
      { applicationId: string; body: FormData }
    >({
      query: ({ applicationId, body }) => ({
        url: `/probationer/${applicationId}/step2`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ProbationerApplications" as any],
    }),

    /**
     * STEP 3
     */
    saveProbationerStep3OLevel: builder.mutation<
      ApiMessage<any[]>,
      { applicationId: string; body: FormData }
    >({
      query: ({ applicationId, body }) => ({
        url: `/probationer/${applicationId}/step3`,
        method: "POST",
        body,

        validateStatus: (response: any) =>
          response.status >= 200 && response.status < 300,
      }),
      invalidatesTags: ["ProbationerApplications" as any],
    }),

    /**
     * STEP 4
     */
    saveProbationerStep4Memberships: builder.mutation<
      ApiMessage<ProbationerProfessionalMembership[]>,
      { applicationId: string; memberships: MembershipItem[] }
    >({
      query: ({ applicationId, memberships }) => ({
        url: `/probationer/${applicationId}/step4`,
        method: "POST",
        body: { memberships },
      }),
      invalidatesTags: ["ProbationerApplications" as any],
    }),

    /**
     * STEP 5
     */
    saveProbationerStep5Experience: builder.mutation<
      ApiMessage<ProbationerWorkExperience[]>,
      { applicationId: string; experiences: ExperienceItem[] }
    >({
      query: ({ applicationId, experiences }) => ({
        url: `/probationer/${applicationId}/step5`,
        method: "POST",
        body: { experiences },
      }),
      invalidatesTags: ["ProbationerApplications" as any],
    }),

    /**
     * STEP 6
     */
    saveProbationerStep6Seminars: builder.mutation<
      ApiMessage<ProbationerSeminar[]>,
      { applicationId: string; seminars: SeminarItem[] }
    >({
      query: ({ applicationId, seminars }) => ({
        url: `/probationer/${applicationId}/step6`,
        method: "POST",
        body: { seminars },
      }),
      invalidatesTags: ["ProbationerApplications" as any],
    }),

    /**
     * STEP 7
     */
    saveProbationerStep7Referees: builder.mutation<
      ApiMessage<ProbationerReferee[]>,
      { applicationId: string; referees: RefereeItem[] }
    >({
      query: ({ applicationId, referees }) => ({
        url: `/probationer/${applicationId}/step7`,
        method: "POST",
        body: { referees },
      }),
      invalidatesTags: ["ProbationerApplications" as any],
    }),

    /**
     * MEMBER SEARCH
     * (leave as-is until you confirm your actual endpoint)
     */
    searchMembers: builder.query<MemberLite[], { q: string }>({
      query: ({ q }) => ({
        url: `/members/search?q=${encodeURIComponent(q)}`,
        method: "GET",
      }),
      // The endpoint returns { status, count, data: [...] } — unwrap to the array,
      // otherwise consumers doing `.map(...)` on the wrapper object crash the page.
      transformResponse: (res: any) =>
        Array.isArray(res) ? res : res?.data ?? [],
    }),

    // Referee/mentor picker (object-arg variant): financially up-to-date members
    // only. Mirrors searchMembers so the panel can swap to it unchanged.
    searchUpToDateMembers: builder.query<MemberLite[], { q: string }>({
      query: ({ q }) => ({
        url: `/members/search-up-to-date?q=${encodeURIComponent(q)}`,
        method: "GET",
      }),
      transformResponse: (res: any) =>
        Array.isArray(res) ? res : res?.data ?? [],
    }),

    /**
     * STEP 8
     */
    acknowledgeProbationerApplication: builder.mutation<
      ApiMessage<ProbationerApplication>,
      { applicationId: string }
    >({
      query: ({ applicationId }) => ({
        url: `/probationer/${applicationId}/step8`,
        method: "POST",
      }),
      invalidatesTags: ["ProbationerApplications" as any],
    }),
    getProbationerApplicationDetails: builder.query<
      ProbationerApplication,
      string
    >({
      query: (id) => ({ url: `/probationer/${id}`, method: "GET" }),
      transformResponse: (res: any) => {
        // allow: {data:{...}} OR {...}
        return (res?.data ?? res) as ProbationerApplication;
      },
      providesTags: (_r, _e, id) => [
        { type: "ProbationerApplications" as any, id },
      ],
    }),

    // example additions (adjust URLs)
    // getProbationerApplicationDetails: builder.query<any, string>({
    //   query: (id) => `/probationer-applications/${id}`,
    //   providesTags: (r, e, id) => [{ type: "ProbationerApplications", id }],
    // }),

    /*
     * approve / adminApprove / reject / adminReject are gone. An entry
     * application is approved or rejected by the Membership Committee, through
     * /committee/entry-applications/{id}/finalize; the endpoints those hooks
     * called now answer 410. Everything the secretariat may do is
     * adminUpdateProbationerStatus below, or returnProbationerApplication.
     */

    /**
     * Move an entry application along the review ladder — the firm-registration
     * SOP: the endorsements land it at `under_review` (the secretariat) on their
     * own, the secretariat sends it to `with_committee`, and `returned` is
     * available at every rung. Approve/reject are NOT admin actions — the
     * committee chairman finalises.
     */
    adminUpdateProbationerStatus: builder.mutation<
      any,
      { id: string; status: string; remark?: string }
    >({
      query: ({ id, ...body }) => ({
        // POST, not PATCH — the host blocks the less common verbs.
        url: `/probationer/admin/${id}/status`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, a) => [
        { type: "ProbationerApplications" as any, id: a.id },
        "ProbationerApplications" as any,
        "ProbationerApplicationDetails" as any,
      ],
    }),

    /**
     * Correct the applicant's email — on the application AND on their login
     * account, which is the half that matters: the address is a credential, and
     * freeing it is the whole reason this exists.
     *
     * The case it solves: a member whose recorded email is wrong applies as a
     * probationer with their real one, and the secretariat then cannot fix the
     * member record because the address is taken by the applicant's own second
     * account.
     *
     * Works at any stage — paid, unpaid, draft or approved.
     */
    adminUpdateProbationerEmail: builder.mutation<any, { id: string; email: string }>({
      query: ({ id, email }) => ({
        // POST, not PATCH — the host blocks the less common verbs.
        url: `/probationer/admin/${id}/email`,
        method: "POST",
        body: { email },
      }),
      invalidatesTags: (_r, _e, a) => [
        { type: "ProbationerApplications" as any, id: a.id },
        "ProbationerApplications" as any,
        "ProbationerApplicationDetails" as any,
      ],
    }),

    // Admin returns the application to the applicant for editing, with a
    // compulsory statement (recorded in the review trail + emailed to applicant).
    returnProbationerApplication: builder.mutation<
      any,
      { applicationId: string; statement: string }
    >({
      query: ({ applicationId, statement }) => ({
        url: `/probationer/admin/${applicationId}/return`,
        method: "POST",
        body: { statement },
      }),
      invalidatesTags: (r, e, a) => [
        { type: "ProbationerApplications", id: a.applicationId },
        "ProbationerApplicationDetails" as any,
      ],
    }),

    acknowledgeProbationerReferee: builder.mutation<
      any,
      { applicationId: string }
    >({
      query: ({ applicationId }) => ({
        url: `/referee/probationer-applications/${applicationId}/acknowledge`,
        method: "POST",
      }),
      invalidatesTags: (r, e, a) => [
        { type: "ProbationerApplications", id: a.applicationId },
      ],
    }),

    refereeApproveProbationerApplication: builder.mutation<
      any,
      { applicationId: string; remarks?: string }
    >({
      query: ({ applicationId, remarks }) => ({
        url: `/probationer/${applicationId}/referee/approve`,
        method: "POST",
        body: { remarks },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "ProbationerApplications", id: arg.applicationId },
      ],
    }),

    refereeRejectProbationerApplication: builder.mutation<
      any,
      { applicationId: string; remarks: string }
    >({
      query: ({ applicationId, remarks }) => ({
        url: `/probationer/${applicationId}/referee/reject`,
        method: "POST",
        body: { remarks },
      }),

      invalidatesTags: (_r, _e, arg) => [
        { type: "ProbationerApplications", id: arg.applicationId },
      ],
    }),

    // add referee(s)
    addProbationerReferees: builder.mutation<
      any,
      { applicationId: string; referees: any[] }
    >({
      query: ({ applicationId, referees }) => ({
        url: `/probationer/${applicationId}/referees`,
        method: "POST",
        body: { referees },
      }),
      invalidatesTags: [
        "ProbationerApplications",
        "ProbationerApplicationDetails",
      ],
    }),

    // replace/update one referee row
    updateProbationerReferee: builder.mutation<
      any,
      {
        applicationId: string;
        old_referee_id: string | null;
        new_member_id: string;
        relationship?: string;
      }
    >({
      query: ({ applicationId, ...body }) => ({
        url: `/probationer/${applicationId}/referees/replace`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        "ProbationerApplications",
        "ProbationerApplicationDetails",
      ],
    }),

    // delete/remove
    deleteProbationerReferee: builder.mutation<
      any,
      { applicationId: string; refereeId: string }
    >({
      query: ({ applicationId, refereeId }) => ({
        url: `/probationer/${applicationId}/referees/${refereeId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "ProbationerApplications",
        "ProbationerApplicationDetails",
      ],
    }),
    getMyRefereeRequests: builder.query<
      RefereeRequest[],
      { decision?: string } | void
    >({
      query: (arg) => {
        const decision = (arg as any)?.decision;
        const qs = decision ? `?status=${encodeURIComponent(decision)}` : "";
        return { url: `/probationer/referee/requests${qs}`, method: "GET" };
      },
      transformResponse: (res: any) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        return [];
      },
      providesTags: ["ProbationerApplications" as any],
    }),

    acceptRefereeRequest: builder.mutation<
      any,
      { refereeId: string; remarks?: string }
    >({
      query: ({ refereeId, remarks }) => ({
        url: `/probationer/referee/requests/${refereeId}/accept`,
        method: "POST",
        body: remarks ? { remarks } : {},
      }),
      invalidatesTags: ["ProbationerApplications" as any],
    }),

    rejectRefereeRequest: builder.mutation<
      any,
      { refereeId: string; remarks: string }
    >({
      query: ({ refereeId, remarks }) => ({
        url: `/probationer/referee/requests/${refereeId}/reject`,
        method: "POST",
        body: { remarks },
      }),
      invalidatesTags: ["ProbationerApplications" as any],
    }),

    getNecApplicationsByCategory: builder.query<
      any,
      { dietId: string; categoryId: number }
    >({
      query: ({ dietId, categoryId }) => {
        return `/probationer/nec?logbook_diet_id=${dietId}&application_category_id=${categoryId}`;
      },

      transformResponse: (res: any) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        return [];
      },
    }),

    getAdminApplicationsByCategoryAndDiet: builder.query<
      any,
      { dietId: string; categoryId: number }
    >({
      query: ({ dietId, categoryId }) => {
        return `/probationer/admin?logbook_diet_id=${dietId}&application_category_id=${categoryId}`;
      },

      transformResponse: (res: any) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        return [];
      },
    }),

    /**
     * TEMPORARY — record the chapter's endorsement on every application in this
     * diet+category that is still waiting for one.
     *
     * For chapters that never log in to endorse their own applicants, leaving a
     * diet stalled. The server refuses to touch applications the chapter HAS
     * endorsed, and only moves one on to the secretariat if the referees and the
     * fee are in — so this fills exactly the one gap, and no more.
     */
    chapterEndorseAllInCategory: builder.mutation<
      any,
      { dietId: string; categoryId: number }
    >({
      query: ({ dietId, categoryId }) => ({
        url: `/probationer/admin/diets/${dietId}/category/${categoryId}/chapter-endorse-all`,
        method: "POST",
      }),
    }),

    /* ── Manual review assignment within a diet ─────────────────────────── */

    /**
     * Panel, load, assignable pool, held-back list and analysis, in one call.
     *
     * `categoryId` narrows the whole answer to one category, which is how the
     * screen is normally opened — a diet's probationers, students and
     * technicians are assigned separately. Omitted, it describes the whole
     * diet, which is what the older links into this screen still ask for.
     *
     * The cache tag carries the category, so a reassignment inside one
     * category does not refetch the others.
     */
    probationerReviewQueue: builder.query<
      any,
      { dietId: string; categoryId?: number | string | null }
    >({
      query: ({ dietId, categoryId }) => {
        const qs =
          categoryId != null && categoryId !== ""
            ? `?application_category_id=${encodeURIComponent(String(categoryId))}`
            : "";
        return `/probationer/admin/review-assignments/diets/${dietId}${qs}`;
      },
      providesTags: (_r, _e, arg) => [
        {
          type: "ProbationerReviewQueue" as const,
          id: `${arg.dietId}:${arg.categoryId ?? "all"}`,
        },
        "ProbationerReviewQueue" as const,
      ],
    }),

    /** Hand a batch of applications to one reviewer. */
    assignProbationerReviewer: builder.mutation<
      any,
      { dietId: string; member_id: string; application_ids: string[] }
    >({
      query: ({ dietId, ...body }) => ({
        url: `/probationer/admin/review-assignments/diets/${dietId}/assign`,
        method: "POST",
        body,
      }),
      // The bare tag, not one keyed by diet: the queue is now cached per
      // diet AND category, and a key naming only the diet matches none of
      // those entries — which left the screen showing the pre-assignment
      // lists until it was reloaded by hand.
      invalidatesTags: ["ProbationerReviewQueue", "ProbationerApplications"],
    }),

    /**
     * Move one application from the reviewer holding it to another committee
     * member, without it passing back through the secretariat.
     */
    reassignProbationerReviewer: builder.mutation<
      any,
      { dietId: string; applicationId: string; member_id: string }
    >({
      query: ({ applicationId, member_id }) => ({
        url: `/probationer/admin/review-assignments/${applicationId}/reassign`,
        method: "POST",
        body: { member_id },
      }),
      invalidatesTags: ["ProbationerReviewQueue", "ProbationerApplications"],
    }),

    unassignProbationerReviewer: builder.mutation<
      any,
      { dietId: string; applicationId: string }
    >({
      query: ({ applicationId }) => ({
        url: `/probationer/admin/review-assignments/${applicationId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProbationerReviewQueue", "ProbationerApplications"],
    }),


    /* necAcceptApplication / necRejectApplication removed: an entry application
       is no longer decided by the assigned reviewer alone. The Membership
       Committee member recommends and the chairman finalises — see
       committeeEntryReviewApi. */

    autoAssignReviewersForActiveExecutiveDiet: builder.mutation<
      {
        message: string;
        assigned_count: number;
        distribution?: Record<string, number>;
      },
      {}
    >({
      query: () => ({
        url: `/v1/admin/reviewer-assignments/active-exec-diet/auto`,
        method: "POST",
      }),
      invalidatesTags: ["ProbationerApplications"],
    }),

    /* ── Entry numbers: student (S), probationer (P), technician (T) ────── */

    /**
     * Where every entry series stands — the last number handed out in each
     * category and what the next one would be. Answers "what is the last
     * number we gave out" for all three at once, which is how it is asked:
     * the series run and drift independently.
     */
    probationerEntrySeries: builder.query<any[], void>({
      query: () => `/probationer/admin/member-numbers/series`,
      transformResponse: (res: any) => res?.data ?? [],
      providesTags: ["ProbationerEntryNumbers" as const],
    }),

    /** Approved applications in a diet (and category), numbered or waiting. */
    probationerMemberNumbers: builder.query<
      any,
      { dietId?: string; categoryId?: number | string | null }
    >({
      query: ({ dietId, categoryId }) => {
        const qs = new URLSearchParams();
        if (dietId) qs.set("logbook_diet_id", dietId);
        if (categoryId != null && categoryId !== "")
          qs.set("application_category_id", String(categoryId));
        const s = qs.toString();
        return `/probationer/admin/member-numbers${s ? `?${s}` : ""}`;
      },
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: ["ProbationerEntryNumbers" as const],
    }),

    /**
     * Give one approved applicant their number.
     *
     * `membership_no` omitted takes the next free number in that category's
     * series; supplied, it uses exactly that number — which is the point of
     * assigning by hand, and how the portal is matched to a number already
     * written on paperwork.
     */
    issueProbationerMemberNumber: builder.mutation<
      any,
      { applicationId: string; membership_no?: string }
    >({
      query: ({ applicationId, membership_no }) => ({
        url: `/probationer/admin/member-numbers/${applicationId}/issue`,
        method: "POST",
        body: membership_no ? { membership_no } : {},
      }),
      // The series moved on, so every row's proposal is now stale — and the
      // applicant is on the register, which the application listings show.
      invalidatesTags: [
        "ProbationerEntryNumbers" as const,
        "ProbationerApplications",
        "ProbationerApplicationDetails" as any,
      ],
    }),

    getSectors: builder.query<any[], void>({
      query: () => "/sectors",
      transformResponse: (res: any) => res.data ?? res,
    }),

    // States
    getStates: builder.query<any[], void>({
      query: () => "/states",
      transformResponse: (res: any) => res.data ?? res,
    }),

    // Local Governments (depends on state)
    getLocalGovernments: builder.query<any[], number>({
      query: (stateId) => `/local-governments?state_id=${stateId}`,
      transformResponse: (res: any) => res.data ?? res,
    }),

    // Chapters (depends on LGA)
    getChapters: builder.query<any[], void>({
      query: () => `/chapters`,
      transformResponse: (res: any) => res.data ?? res,
    }),

    // Applications analysis. Scope is OPTIONAL at every level: no diet means
    // every application the institute has ever taken, a diet narrows it to that
    // intake, a category narrows it again. The standalone analysis page starts
    // at the widest of the three, the diet screens open it already narrowed.
    getApplicationsAnalysis: builder.query<
      any,
      {
        dietId?: string;
        categoryId?: number;
        status?: string;
        year?: string;
        month?: string;      // pass numeric string "1"-"12" (already converted by caller)
        age_range?: string;
        gender?: string;
        whois?: string;
        state_id?: string;
        chapter_id?: string;
        sector_id?: string;
      }
    >({
      query: ({ dietId, categoryId, status, year, month, age_range, gender, whois, state_id, chapter_id, sector_id }) => {
        const qs = new URLSearchParams();
        if (dietId)     qs.set("logbook_diet_id", dietId);
        if (categoryId  != null) qs.set("application_category_id", String(categoryId));
        if (status)     qs.set("status",     status);
        if (year)       qs.set("year",       year);
        if (month)      qs.set("month",      month);
        if (age_range)  qs.set("age_range",  age_range);
        if (gender)     qs.set("gender",     gender);
        if (whois)      qs.set("whois",      whois);
        if (state_id)   qs.set("state_id",   state_id);
        if (chapter_id) qs.set("chapter_id", chapter_id);
        if (sector_id)  qs.set("sector_id",  sector_id);
        return `/probationer/admin/analysis?${qs.toString()}`;
      },
      transformResponse: (res: any) => res?.data ?? res,
    }),
  }),
});

export const {
  useGetMyProbationerApplicationsQuery,
  useGetProbationerApplicationDetailsQuery,
  useAdminUpdateProbationerStatusMutation,
  useAdminUpdateProbationerEmailMutation,
  useReturnProbationerApplicationMutation,

  useAcknowledgeProbationerRefereeMutation,
  useCreateProbationerApplicationStep1Mutation,
  useSaveProbationerStep2QualificationsMutation,
  useSaveProbationerStep3OLevelMutation,
  useSaveProbationerStep4MembershipsMutation,
  useSaveProbationerStep5ExperienceMutation,
  useSaveProbationerStep6SeminarsMutation,
  useSaveProbationerStep7RefereesMutation,
  useSearchMembersQuery,
  useLazySearchMembersQuery,
  useLazySearchUpToDateMembersQuery,
  useAcknowledgeProbationerApplicationMutation,
  useGetMyRefereeRequestsQuery,
  useAcceptRefereeRequestMutation,
  useRejectRefereeRequestMutation,
  useAddProbationerRefereesMutation,
  useUpdateProbationerRefereeMutation,
  useDeleteProbationerRefereeMutation,
  useRefereeRejectProbationerApplicationMutation,
  useRefereeApproveProbationerApplicationMutation,

  useLazySearchMembersByQQuery,
  useLazySearchUpToDateMembersByQQuery,
  useGetActiveUserCategoriesQuery,
  useGetDietCategoryCountsQuery,
  useGetNecApplicationsByCategoryQuery,
  useGetAdminApplicationsByCategoryQuery,
  useGetAdminApplicationsByCategoryAndDietQuery,
  useChapterEndorseAllInCategoryMutation,
  useProbationerReviewQueueQuery,
  useAssignProbationerReviewerMutation,
  useReassignProbationerReviewerMutation,
  useUnassignProbationerReviewerMutation,
  useAutoAssignReviewersForActiveExecutiveDietMutation,
  useProbationerEntrySeriesQuery,
  useProbationerMemberNumbersQuery,
  useIssueProbationerMemberNumberMutation,
  useGetSectorsQuery,
  useGetStatesQuery,
  useGetLocalGovernmentsQuery,
  useGetChaptersQuery,
  useGetApplicationsAnalysisQuery,
} = probationerApplicationApi;

/**
 * =========
 * SELECTOR: get one application by id (for details page)
 * Usage:
 *   const app = useGetProbationerApplicationById(id)
 * =========
 */
export const useGetProbationerApplicationById = (id?: string) => {
  return probationerApplicationApi.endpoints.getMyProbationerApplications.useQueryState(
    undefined,
    {
      selectFromResult: ({ data, isLoading, error }) => ({
        isLoading,
        error,
        app: data?.find((x) => x.id === id),
      }),
    }
  );
};
